import { forwardRef, useEffect, useState, type CSSProperties } from 'react';

/**
 * Input numérico locale-friendly:
 *  • Acepta tanto coma como punto como separador decimal.
 *  • Acepta los signos + y -.
 *  • Muestra los números formateados con coma (locale es-ES).
 *  • Si `decimals` está definido, redondea y rellena con ese nº de decimales
 *    al perder el foco (típico para EUR/USD: siempre 2 decimales).
 *
 * Internamente la prop `value` y el callback `onChange` operan con `number`
 * estándar (punto), igual que cualquier otro input controlado.
 */
export type DecimalInputProps = {
  value: number;
  onChange: (n: number) => void;
  /** Si está definido, redondea/rellena a este nº de decimales onBlur. */
  decimals?: number;
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
};

function parseDecimal(raw: string): number | null {
  if (raw === '' || raw === '-' || raw === '+' || raw === ',' || raw === '.') return null;
  const normalized = raw.trim().replace(',', '.');
  if ((normalized.match(/\./g) || []).length > 1) return null;
  if (!/^[+-]?\d*\.?\d*$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function formatDisplay(n: number, decimals?: number): string {
  if (!Number.isFinite(n)) return '';
  if (decimals != null) return n.toFixed(decimals).replace('.', ',');
  return String(n).replace('.', ',');
}

export const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(function DecimalInput(
  { value, onChange, decimals, className = '', style, placeholder, disabled, readOnly, ariaLabel },
  ref,
) {
  const [display, setDisplay] = useState<string>(() => formatDisplay(value, decimals));
  const [editing, setEditing] = useState(false);

  // Si el valor externo cambia (otra fuente puso el state), sincroniza el
  // display, pero sólo si el usuario no está escribiendo (para no pisar
  // entradas parciales como "1,").
  useEffect(() => {
    if (!editing) setDisplay(formatDisplay(value, decimals));
  }, [value, decimals, editing]);

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={ariaLabel}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      value={display}
      onFocus={() => setEditing(true)}
      onChange={(e) => {
        const raw = e.target.value;
        // Sólo permitimos caracteres válidos: dígitos, + - , .
        const cleaned = raw.replace(/[^0-9+\-.,]/g, '');
        setDisplay(cleaned);
        const parsed = parseDecimal(cleaned);
        if (parsed != null) onChange(parsed);
      }}
      onBlur={() => {
        setEditing(false);
        const parsed = parseDecimal(display);
        const safe = parsed ?? 0;
        const rounded = decimals != null ? Number(safe.toFixed(decimals)) : safe;
        setDisplay(formatDisplay(rounded, decimals));
        if (rounded !== value) onChange(rounded);
      }}
      className={`block rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500 read-only:bg-slate-50 read-only:text-slate-700 ${className}`}
      style={style}
    />
  );
});
